/**
 * TextField component.
 *
 * Standard text input with label, required indicator,
 * and inline validation error display.
 */

import type { FormFieldDefinition } from "../../types";

export interface TextFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class TextField {
  private readonly element: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly errorEl: HTMLElement;

  constructor(private readonly options: TextFieldOptions) {
    const { field } = options;

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = field.label;
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // Input
    this.inputEl = document.createElement("input");
    this.inputEl.type = field.type === "email" ? "email" : "text";
    this.inputEl.id = `hana-field-${field.name}`;
    this.inputEl.className = "hana-form-field__input";
    this.inputEl.name = field.name;
    this.inputEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.inputEl.setAttribute("aria-required", "true");
    }
    if (field.placeholder) {
      this.inputEl.placeholder = field.placeholder;
    }
    if (field.value) {
      this.inputEl.value = field.value;
    }

    this.inputEl.addEventListener("input", () => {
      this.clearError();
      this.options.onChange(field.name, this.inputEl.value);
    });

    this.element.appendChild(this.inputEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.inputEl.value.trim();
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.inputEl.classList.add("hana-form-field__input--error");
    this.inputEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.inputEl.classList.remove("hana-form-field__input--error");
    this.inputEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.inputEl.disabled = disabled;
  }

  focus(): void {
    this.inputEl.focus();
  }
}
